import ContextMenu from '@/components/common/ContextMenu';
import InstrumentInfoContext from '@/components/common/InstrumentInfoContext';
import { Modify } from '@/model/common';
import { useAppSelector } from '@/state/hooks';
import { selectCurrentAccount } from '@/state/reducers/authSlice';
import { BalancesSummaryReturn, BalanceSummary } from '@/types/sso';
import useContextMenu from '@/utils/hooks/useContextMenu';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import useLanguage from '@/utils/hooks/useLanguage';
import usePortalBalancesTable from '@/utils/hooks/usePortalBalancesTable';
import { useSso } from '@/utils/providers/SSOProvider';
import cn from 'classnames';
import { memo, useState } from 'react';
import { MdRefresh } from 'react-icons/md';
import PortalVirtualizedTable from '../table/PortalVirtualizedTable';

export interface PortalBalancesRow
    extends Modify<
        BalanceSummary,
        {
            tableCurrency: any;
        }
    > {}

export interface PortalBalancesTableProps {
    balances?: BalancesSummaryReturn;
    baseCurrency: string;
    isHistoric?: boolean;
    showRefresh?: boolean;
    onDeposit?: (currency: string) => void;
    onWithdraw?: (currency: string) => void;
}

export type PositionFilter = 'Show All' | 'Hide Closed Positions';

const PortalBalancesTable = (props: PortalBalancesTableProps) => {
    const { showRefresh = false, isHistoric = false } = props;

    const { summaryApi } = useSso();

    const lang = useLanguage();
    const contextMenu = useContextMenu();
    const currentAccount = useAppSelector(selectCurrentAccount);

    const { contextMenuX, contextMenuY, contextMenuVisible, handleOpenContextMenu, handleCloseContextMenu } =
        contextMenu;

    const [stateLoading, setStateLoading] = useState<boolean>(false);
    const [instrumentInfoContext, setInstrumentInfoContext] = useState();

    const { debouncedTableInstance, columnSizing, sorting, formattedPortalBalances, expandedRows, globalFilter } =
        usePortalBalancesTable({
            ...props,
            setInstrumentInfoContext,
            handleOpenContextMenu
        });

    useDidUpdate(() => {
        setStateLoading(true);
        setTimeout(() => setStateLoading(false), 1000);
    }, [currentAccount]);

    return (
        <div className="flex flex-col gap-1 bg-brand-background-dark p-4 sm:p-6 py-4 border border-brand-neutral-700 text-brand-neutral-200 rounded-md overflow-hidden max-h-[750px]">
            <div className="flex flex-col h-full gap-4">
                <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center flex-wrap gap-2 sm:gap-5">
                        <div className="">{isHistoric ? lang.portal.history : lang.portal.currentBalances}</div>

                        {showRefresh && (
                            <div
                                className={cn('border border-brand-neutral-700 rounded-md p-1.5 cursor-pointer', {
                                    'hover:bg-brand-primary': !summaryApi.isLoading && !summaryApi.isValidating
                                })}
                                onClick={() => {
                                    if (!summaryApi.isLoading && !summaryApi.isValidating) {
                                        summaryApi.mutate();
                                    }
                                }}>
                                <MdRefresh
                                    className={cn('w-4 h-4', {
                                        'animate-spin text-brand-neutral-400':
                                            summaryApi.isLoading || summaryApi.isValidating
                                    })}
                                />
                            </div>
                        )}
                    </div>
                </div>
                <MemoizedPortalBalancesTable
                    balances={formattedPortalBalances}
                    expandedRows={expandedRows}
                    globalFilter={globalFilter}
                    tableInstance={debouncedTableInstance}
                    columnSizing={columnSizing}
                    sorting={sorting}
                    currentAccount={currentAccount}
                />
            </div>
            <ContextMenu
                x={contextMenuX}
                y={contextMenuY}
                offset={{ x: 4, y: 4 }}
                visible={contextMenuVisible}
                onClose={handleCloseContextMenu}
                className="!bg-brand-background !w-auto">
                <InstrumentInfoContext market={instrumentInfoContext} handleCloseContextMenu={handleCloseContextMenu} />
            </ContextMenu>
        </div>
    );
};

export default PortalBalancesTable;

const MemoizedPortalBalancesTable = memo(({ tableInstance }: any) => {
    // globalFilterEnabled
    return <PortalVirtualizedTable tableInstance={tableInstance} isLoadingTableContent={false} />;
});

MemoizedPortalBalancesTable.displayName = 'PortalBalancesTable';
